import {
  onUpdateLineItem,
  addLineItems,
  clearCart,
  getDonationCategory,
  applyDiscountCodes,
  getCart,
} from '@/cart/cart-store';
import {
  GwpBundle,
  GwpVariant,
} from '@/components/buy-stack/gwp-buy-stack/types';
import {getFeatureFlag} from '@/feature-flags/feature-flags-store';
import {useNavigate} from '@/hooks/use-navigate';
import {parseGid} from '@shopify/hydrogen-react';
import {cloneElement, toChildArray, VNode} from 'preact';
import {TargetedEvent} from 'preact/compat';
import {useEffect, useRef, useState} from 'preact/hooks';
import {endDrawer} from '@/components/drawers';
import {logError} from '@/utilities/log';
import {LineItemInput} from '@/cart/types';

export type GwpBuyStackProps = {
  bundles: GwpBundle[];
  children: VNode;
  variants: GwpVariant[];
  onUpdateSelectedVariant?: (variant: GwpVariant | null) => void;
};

type FormNode = VNode<{
  dataSelectedVariantId: string;
  dataSelectedBundle: string;
}>;

const isFormNode = (child: VNode | string | number): child is FormNode =>
  typeof child !== 'string' &&
  typeof child !== 'number' &&
  child.type === 'form';

const parseId = (gid: string) => Number(parseGid(gid).id);

/**
 * A custom element that wraps the gwp-buy-stack form.
 * It updates the selected variants id and selected bundle when the form data changes
 * and adds the selected variants to the cart when the form is submitted,
 * then goes to checkout with the bundle discounts.
 *
 * @param bundles - The GWP bundle metaobjects
 * @param variants - The product variants
 * @param children - The form
 */
export default function GwpBuyStack({
  bundles,
  children,
  variants,
  onUpdateSelectedVariant,
}: GwpBuyStackProps) {
  const shouldOpenCartDrawer = getFeatureFlag('gwp-drawer') === 'true';
  const navigate = useNavigate();
  const [formData, setFormData] = useState(new FormData());
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const maybeClearCart = async () => {
    const itemCount = getCart()?.item_count;
    if (itemCount) {
      await clearCart();
    }
  };

  useEffect(() => {
    onUpdateLineItem((_, {quantity}) => {
      if (quantity === 0) maybeClearCart().catch(logError);
    });
  }, []);

  const bundleName = formData.get('bundle');
  const selectedBundle =
    bundles.find(({name}) => name === bundleName) ?? bundles[0];
  const bundleVariantIds = selectedBundle?.main_variants?.map(parseId);
  const option = formData.get('first-option') as string | null;
  const isBundleVariant = ({id}: GwpVariant) => bundleVariantIds?.includes(id);
  const isSelected = ({options}: GwpVariant) =>
    option && options.includes(option);
  const selectedVariant =
    variants.filter(isBundleVariant).find(isSelected) ?? variants[0];
  const selectedVariantId = selectedVariant?.id;

  useEffect(() => {
    const {selling_plan_allocations, ...rest} = selectedVariant;
    onUpdateSelectedVariant?.({
      ...rest,
      // Don't allow subscriptions to bundles that include gifts or discounts
      selling_plan_allocations:
        !!selectedBundle?.gift_variants?.length ||
        !!selectedBundle?.discount_codes?.length
          ? []
          : selling_plan_allocations,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantId]);

  const handleChange = (e: Event) => {
    const target = e.currentTarget as HTMLFormElement | null;

    if (!target) return;

    setFormData(new FormData(target));
  };

  const handleCheckout = async () => {
    if (!selectedVariantId) return;

    setSubmitting(true);
    const donation = getDonationCategory() ?? 'Homeless Shelters';
    const properties = {
      _buy_one_give_ten_category: donation,
      _max_quantity: '1',
    };
    const createItem = (id: number): LineItemInput => ({
      id,
      quantity: 1,
      properties,
    });
    const mainItem = createItem(selectedVariantId);
    const bundleGiftIds = selectedBundle.gift_variants?.map(parseId);
    const gifts = bundleGiftIds?.map(createItem) ?? [];
    const discounts = selectedBundle.discount_codes;

    const data = new FormData(formRef.current ?? undefined);
    const sellingPlan = data.get('selling-plan');
    const subscribeAndSave = data.get('subscribe-and-save');
    mainItem.selling_plan =
      sellingPlan && subscribeAndSave ? Number(sellingPlan) : undefined;

    await Promise.all([
      maybeClearCart(),
      addLineItems([...gifts, mainItem]),
      discounts && discounts.length > 0 && applyDiscountCodes(discounts),
    ]);
    setSubmitting(false);
  };

  const handleSubmit = async (e: TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleCheckout();

    if (shouldOpenCartDrawer) {
      endDrawer.open();
    } else {
      await handleCheckout();
      navigate(`/checkout`);
    }
  };

  const formNode = toChildArray(children.props.children).find(isFormNode);
  if (!formNode) return null;

  const props = {
    ref: formRef,
    'data-selected-variant-id':
      selectedVariantId ?? formNode.props.dataSelectedVariantId,
    'data-selected-bundle':
      selectedBundle?.name ?? formNode.props.dataSelectedBundle,
    onChange: handleChange,
    onSubmit: handleSubmit,
    style: {
      '--btn-opacity': submitting ? 0 : 1,
      '--loading-indicator-opacity': submitting ? 1 : 0,
    },
  };

  return cloneElement(formNode, props);
}
